// Bottom-right, non-blocking toast that surfaces invalid lifecycle
// transitions. Educational rather than punitive — tells the operator
// the rejected pair AND the valid set from the current stage so they
// can pick a reachable target without hunting through the matrix.
// Auto-dismisses (~4.5s) but parent can also dismiss imperatively.

import React, { useEffect } from 'react';

import type { LifecycleStage } from '../../../types/contact';
import { LIFECYCLE_STAGE_LABELS } from '../../../types/contact';

const AUTO_DISMISS_MS = 4500;

export interface InvalidTransition {
  from: LifecycleStage;
  to: LifecycleStage;
  valid: readonly LifecycleStage[];
}

export interface ContactInvalidTransitionToastProps {
  error: InvalidTransition | null;
  onDismiss: () => void;
}

const ContactInvalidTransitionToast: React.FC<ContactInvalidTransitionToastProps> = ({
  error,
  onDismiss,
}) => {
  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [error, onDismiss]);

  if (!error) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 max-w-sm bg-surface-1 border-[1.5px] border-danger rounded-ds-lg px-4 py-3.5 shadow-ds-e3"
    >
      <div className="font-mono text-[11px] uppercase tracking-wider text-danger-text mb-1">
        Invalid transition
      </div>
      <div className="text-[13px] text-slate-50 mb-1.5">
        <code className="font-mono">{LIFECYCLE_STAGE_LABELS[error.from]}</code>{' '}
        →{' '}
        <code className="font-mono">{LIFECYCLE_STAGE_LABELS[error.to]}</code>{' '}
        isn’t allowed.
      </div>
      <div className="text-[11px] text-slate-400 leading-relaxed">
        valid from{' '}
        <code className="font-mono text-slate-300">
          {LIFECYCLE_STAGE_LABELS[error.from]}
        </code>
        :{' '}
        {error.valid.length === 0 ? (
          <span className="text-slate-500">none · terminal</span>
        ) : (
          error.valid.map((v, i) => (
            <React.Fragment key={v}>
              {i > 0 ? ' ' : ''}
              <code className="font-mono text-slate-300 mr-1">{v}</code>
            </React.Fragment>
          ))
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-1 right-2 text-slate-500 hover:text-slate-200 text-lg leading-none"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default ContactInvalidTransitionToast;
